<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable no-use-before-define */
import { computed, reactive, ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisSettingsAPI from 'dashboard/api/laisSettings';
import BrandVoiceEditor from '../../brandVoice/pages/BrandVoiceEditor.vue';
import MinhaLaisAtendimentoPanel from '../components/MinhaLaisAtendimentoPanel.vue';
import MinhaLaisReengajamentoSection from '../components/MinhaLaisReengajamentoSection.vue';
import MinhaLaisQualificationQuestionsCard from '../components/MinhaLaisQualificationQuestionsCard.vue';
import MinhaLaisBrandVoiceSummaryCard from '../components/MinhaLaisBrandVoiceSummaryCard.vue';
import MinhaLaisTaxaServicoCard from '../components/MinhaLaisTaxaServicoCard.vue';
import MinhaLaisCrmPanel from '../components/MinhaLaisCrmPanel.vue';
import MinhaLaisWhatsappQrPanel from '../components/MinhaLaisWhatsappQrPanel.vue';
import MinhaLaisWhatsappOnboardingPanel from '../components/MinhaLaisWhatsappOnboardingPanel.vue';
import MinhaLaisWhatsappProfilePanel from '../components/MinhaLaisWhatsappProfilePanel.vue';
import MinhaLaisWhatsappOperationsPanel from '../components/MinhaLaisWhatsappOperationsPanel.vue';
import MinhaLaisOrigensCard from '../components/MinhaLaisOrigensCard.vue';
import MinhaLaisNotificacoesCard from '../components/MinhaLaisNotificacoesCard.vue';
import MinhaLaisAparenciaCard from '../components/MinhaLaisAparenciaCard.vue';
import { useMinhaLaisDarkMode } from '../composables/useMinhaLaisDarkMode';
import { useMinhaLaisNotifications } from '../composables/useMinhaLaisNotifications';
import { useMinhaLaisCrmConfig } from '../composables/useMinhaLaisCrmConfig';
import { useMinhaLaisQualificationQuestions } from '../composables/useMinhaLaisQualificationQuestions';
import { useMinhaLaisWhatsappConnections } from '../composables/useMinhaLaisWhatsappConnections';
import { useMinhaLaisWhatsappGovernance } from '../composables/useMinhaLaisWhatsappGovernance';
import { createMinhaLaisSettingsForm } from '../utils/minhaLaisSettingsForm';
import { connectionRemovalLabel } from '../utils/minhaLaisPresenters';
import {
  DEFAULT_TAB,
  focusElementById,
  nextKeyboardTabIndex,
  normalizeTab,
  normalizeWhatsappSubTab,
  shouldPrimeLegacyNotifications,
  tabButtonId,
  tabPanelId,
  whatsappSubTabButtonId,
  whatsappSubTabPanelId,
} from '../utils/minhaLaisTabs';
import { omitCanonicalBrandVoiceFields } from './canonicalBrandVoiceFields';

const route = useRoute();
const router = useRouter();
const account = useAccount();
const tab = ref(normalizeTab(route.query.tab));
const whatsappSubTab = ref(
  normalizeWhatsappSubTab(route.query.whatsapp_subtab)
);
const isSaving = ref(false);
const canUpdateSettings = ref(true);
const brandVoiceRoute = computed(() =>
  account.accountScopedRoute('minha_lais_index', {}, { tab: 'brand_voice' })
);

const form = reactive(createMinhaLaisSettingsForm());

const { darkModeEnabled, toggleDarkMode, initializeDarkMode } =
  useMinhaLaisDarkMode();

const {
  notificationSearch,
  notificationContactId,
  notificationProfile,
  consents,
  isSavingConsents,
  notificationContactOptions,
  selectedNotificationContact,
  carregarNotificationContacts,
  ensureNotificationTabReady,
  onNotificationContactChange,
  selectNotificationProfile,
  salvarConsent,
  aplicarTodosOsPerfis,
  trackTooltipClick,
} = useMinhaLaisNotifications();

const {
  crmConfig,
  crmProviderOptions,
  crmHelp,
  isLoadingCrm,
  isSavingCrm,
  isTestingCrm,
  isActivatingCrm,
  carregarCrmConfig,
  salvarCrmConfig,
  testarCrmConfig,
  ativarCrmConfig,
} = useMinhaLaisCrmConfig();

const {
  qualificationQuestions,
  qualificationQuestionLimits,
  newQualificationQuestion,
  isLoadingQualificationQuestions,
  savingQualificationQuestionId,
  deletingQualificationQuestionId,
  carregarQualificationQuestions,
  criarQualificationQuestion,
  salvarQualificationQuestion,
  removerQualificationQuestion,
} = useMinhaLaisQualificationQuestions({ canUpdateSettings });

const isWhatsappQrViewActive = () =>
  tab.value === 'whatsapp' && whatsappSubTab.value === 'conexao_qr';

const {
  whatsappConnections,
  whatsappMeta,
  whatsappQr,
  whatsappSelectedOwnerId,
  isLoadingWhatsappConnections,
  isGeneratingWhatsappQr,
  refreshingConnectionId,
  updatingConnectionId,
  deletingConnectionId,
  connectionToRemove,
  whatsappAgentOptions,
  whatsappConnectionOptions,
  activeWhatsappConnections,
  isWhatsappPairingBusy,
  carregarWhatsappConnections,
  gerarWhatsappQr,
  renovarWhatsappQr,
  trocarResponsavelWhatsapp,
  removerWhatsappConnection,
  cancelarRemoverWhatsappConnection,
  confirmarRemoverWhatsappConnection,
  stopWhatsappQrAutoRefresh,
  handleWhatsappQrVisibilityChange,
} = useMinhaLaisWhatsappConnections({ isQrViewActive: isWhatsappQrViewActive });

const {
  whatsappGovernance,
  whatsappChecklistItems,
  whatsappChecklistProgress,
  whatsappPhotoFile,
  whatsappRequestedDisplayName,
  isLoadingWhatsappGovernance,
  isSavingWhatsappOnboarding,
  isSavingWhatsappProfile,
  isUploadingWhatsappPhoto,
  isRequestingWhatsappDisplayName,
  carregarWhatsappGovernance,
  salvarWhatsappOnboarding,
  salvarWhatsappProfile,
  selecionarWhatsappPhoto,
  enviarWhatsappProfilePhoto,
  solicitarWhatsappDisplayName,
} = useMinhaLaisWhatsappGovernance();

const carregar = async () => {
  try {
    const { data } = await LaisSettingsAPI.get();
    canUpdateSettings.value = Boolean(data.can_update);
    Object.assign(form, omitCanonicalBrandVoiceFields(data));
    if (!canUpdateSettings.value && whatsappSubTab.value !== 'conexao_qr') {
      whatsappSubTab.value = 'conexao_qr';
      syncWhatsappSubTabQuery('conexao_qr');
    }
    if (!canUpdateSettings.value && tab.value !== 'whatsapp') {
      await selectTab('whatsapp');
    }
  } catch (error) {
    useAlert('Não foi possível carregar as configurações agora.');
  }
};

const salvar = async () => {
  if (!canUpdateSettings.value) {
    useAlert('Apenas administradores podem salvar essas configurações.');
    return;
  }

  isSaving.value = true;
  try {
    await LaisSettingsAPI.update(omitCanonicalBrandVoiceFields(form));
    useAlert('Configurações salvas com sucesso');
  } catch (e) {
    useAlert('Não foi possível salvar as configurações agora.');
  } finally {
    isSaving.value = false;
  }
};

const whatsappSubTabs = computed(() => {
  const tabs = [{ id: 'conexao_qr', label: 'Conexão QR' }];
  if (canUpdateSettings.value) {
    tabs.push(
      { id: 'onboarding_meta', label: 'Onboarding Meta' },
      { id: 'perfil', label: 'Perfil' },
      { id: 'operacoes', label: 'Operações' }
    );
  }
  return tabs;
});

const mainTabs = computed(() => {
  const tabs = [
    { id: 'geral', label: 'Geral' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'brand_voice', label: 'Brand Voice' },
    { id: 'automacoes_ia', label: 'Automações/IA' },
    { id: 'testes', label: 'Testes' },
  ];

  return canUpdateSettings.value
    ? tabs
    : tabs.filter(item => item.id === 'whatsapp');
});

const syncTabQuery = nextTab => {
  const nextQuery = { ...route.query };
  if (nextTab === DEFAULT_TAB) {
    delete nextQuery.tab;
  } else {
    nextQuery.tab = nextTab;
  }

  router.replace({ query: nextQuery }).catch(() => {});
};

const syncWhatsappSubTabQuery = nextSubTab => {
  const nextQuery = { ...route.query, tab: 'whatsapp' };
  if (nextSubTab === 'conexao_qr') {
    delete nextQuery.whatsapp_subtab;
  } else {
    nextQuery.whatsapp_subtab = nextSubTab;
  }

  router.replace({ query: nextQuery }).catch(() => {});
};

const carregarWhatsappSubTab = async normalizedSubTab => {
  if (normalizedSubTab === 'conexao_qr') {
    await carregarWhatsappConnections();
    return;
  }

  stopWhatsappQrAutoRefresh();

  if (normalizedSubTab === 'perfil') {
    await carregarWhatsappConnections({ autoLoadQr: false, silent: true });
    await carregarWhatsappGovernance({ view: 'profile' });
    return;
  }

  if (normalizedSubTab === 'onboarding_meta') {
    await carregarWhatsappGovernance({ view: 'onboarding' });
    return;
  }

  if (normalizedSubTab === 'operacoes') {
    await carregarWhatsappConnections({ autoLoadQr: false, silent: true });
  }
};

const prepareTab = async (normalizedTab, sourceTab = normalizedTab) => {
  if (normalizedTab === 'geral' && shouldPrimeLegacyNotifications(sourceTab)) {
    await ensureNotificationTabReady();
  }
  if (normalizedTab === 'automacoes_ia') {
    await carregarQualificationQuestions();
  }
  if (normalizedTab === 'testes') {
    await carregarCrmConfig();
  }
  if (normalizedTab === 'whatsapp') {
    await carregarWhatsappSubTab(whatsappSubTab.value);
  } else {
    stopWhatsappQrAutoRefresh();
  }
};

const selectTab = async nextTab => {
  const normalizedTab = normalizeTab(nextTab);
  tab.value = normalizedTab;
  syncTabQuery(normalizedTab);
  await prepareTab(normalizedTab, nextTab);
};

const handleMainTabKeydown = async (event, currentTabId) => {
  const tabs = mainTabs.value;
  const currentIndex = tabs.findIndex(item => item.id === currentTabId);
  const nextIndex = nextKeyboardTabIndex(event, currentIndex, tabs.length);
  if (nextIndex === null || currentIndex === -1) return;

  event.preventDefault();
  const nextTab = tabs[nextIndex];
  await selectTab(nextTab.id);
  focusElementById(tabButtonId(nextTab.id));
};

const selectWhatsappSubTab = async nextSubTab => {
  const normalizedSubTab = normalizeWhatsappSubTab(nextSubTab);
  whatsappSubTab.value = normalizedSubTab;
  syncWhatsappSubTabQuery(normalizedSubTab);
  await carregarWhatsappSubTab(normalizedSubTab);
};

const handleWhatsappSubTabKeydown = async (event, currentSubTabId) => {
  const tabs = whatsappSubTabs.value;
  const currentIndex = tabs.findIndex(item => item.id === currentSubTabId);
  const nextIndex = nextKeyboardTabIndex(event, currentIndex, tabs.length);
  if (nextIndex === null || currentIndex === -1) return;

  event.preventDefault();
  const nextSubTab = tabs[nextIndex];
  await selectWhatsappSubTab(nextSubTab.id);
  focusElementById(whatsappSubTabButtonId(nextSubTab.id));
};

watch(
  () => route.query.tab,
  async value => {
    const normalizedTab = normalizeTab(value);
    if (normalizedTab === tab.value) {
      if (normalizedTab === 'geral' && shouldPrimeLegacyNotifications(value)) {
        await ensureNotificationTabReady();
      }
      return;
    }
    tab.value = normalizedTab;
    await prepareTab(normalizedTab, value);
  }
);

watch(
  () => route.query.whatsapp_subtab,
  async value => {
    const normalizedSubTab = normalizeWhatsappSubTab(value);
    if (normalizedSubTab === whatsappSubTab.value) return;
    whatsappSubTab.value = normalizedSubTab;
    if (tab.value === 'whatsapp')
      await carregarWhatsappSubTab(normalizedSubTab);
  }
);

onMounted(async () => {
  if (typeof document !== 'undefined') {
    document.addEventListener(
      'visibilitychange',
      handleWhatsappQrVisibilityChange
    );
  }

  initializeDarkMode();
  await carregar();
  await prepareTab(tab.value, route.query.tab);
});

onUnmounted(() => {
  stopWhatsappQrAutoRefresh();
  if (typeof document !== 'undefined') {
    document.removeEventListener(
      'visibilitychange',
      handleWhatsappQrVisibilityChange
    );
  }
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex flex-col h-full bg-white font-redHatText overflow-hidden"
  >
    <LaisHeader
      title="Minha Lais"
      helper="Configure como a Lais deve atender, qualificar e se comunicar com seus clientes."
    >
      <template #actions>
        <LaisPrimaryButton
          v-if="canUpdateSettings && tab !== 'brand_voice'"
          variant="primary"
          icon="i-lucide-check"
          :is-loading="isSaving"
          @click="salvar"
        >
          Salvar configurações
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <!-- Tabs -->
    <div class="border-b border-[#e5e7eb] px-4 sm:px-6 lg:px-10">
      <div
        class="flex items-center gap-6 overflow-x-auto"
        role="tablist"
        aria-label="Seções de Minha Lais"
      >
        <button
          v-for="mainTab in mainTabs"
          :id="tabButtonId(mainTab.id)"
          :key="mainTab.id"
          type="button"
          role="tab"
          :aria-selected="tab === mainTab.id"
          :aria-controls="tabPanelId(mainTab.id)"
          :tabindex="tab === mainTab.id ? 0 : -1"
          class="relative py-3 text-[14px] font-semibold transition-colors whitespace-nowrap"
          :class="
            tab === mainTab.id
              ? 'text-lais-primary'
              : 'text-lais-text-muted hover:text-lais-text-body'
          "
          @click="selectTab(mainTab.id)"
          @keydown="event => handleMainTabKeydown(event, mainTab.id)"
        >
          {{ mainTab.label }}
          <span
            v-if="tab === mainTab.id"
            class="absolute bottom-0 left-0 right-0 h-[3px] bg-lais-primary rounded-t"
          />
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-auto p-4 sm:p-6 lg:p-10">
      <!-- Tab Geral -->
      <div
        v-if="tab === 'geral'"
        :id="tabPanelId('geral')"
        role="tabpanel"
        :aria-labelledby="tabButtonId('geral')"
        tabindex="0"
        class="grid w-full gap-5"
      >
        <MinhaLaisAtendimentoPanel v-model="form" />
      </div>

      <!-- Tab Automações/IA -->
      <div
        v-if="tab === 'automacoes_ia'"
        :id="tabPanelId('automacoes_ia')"
        role="tabpanel"
        :aria-labelledby="tabButtonId('automacoes_ia')"
        tabindex="0"
        class="grid w-full gap-5"
      >
        <MinhaLaisReengajamentoSection v-model="form" />

        <MinhaLaisQualificationQuestionsCard
          v-model:qualification-questions="qualificationQuestions"
          v-model:new-qualification-question="newQualificationQuestion"
          :qualification-question-limits="qualificationQuestionLimits"
          :is-loading-qualification-questions="isLoadingQualificationQuestions"
          :can-update-settings="canUpdateSettings"
          :saving-qualification-question-id="savingQualificationQuestionId"
          :deleting-qualification-question-id="deletingQualificationQuestionId"
          @refresh="carregarQualificationQuestions"
          @create="criarQualificationQuestion"
          @save="salvarQualificationQuestion"
          @remove="removerQualificationQuestion"
        />

        <MinhaLaisBrandVoiceSummaryCard :brand-voice-route="brandVoiceRoute" />

        <MinhaLaisTaxaServicoCard v-model="form" />
      </div>

      <!-- Tab Brand Voice -->
      <div
        v-if="tab === 'brand_voice'"
        :id="tabPanelId('brand_voice')"
        role="tabpanel"
        :aria-labelledby="tabButtonId('brand_voice')"
        tabindex="0"
        class="-m-4 sm:-m-6 lg:-m-10"
      >
        <BrandVoiceEditor embedded />
      </div>

      <!-- Tab Testes -->
      <div
        v-if="tab === 'testes'"
        :id="tabPanelId('testes')"
        role="tabpanel"
        :aria-labelledby="tabButtonId('testes')"
        tabindex="0"
        class="grid w-full gap-5"
      >
        <MinhaLaisCrmPanel
          v-model="crmConfig"
          :crm-provider-options="crmProviderOptions"
          :crm-help="crmHelp"
          :is-loading-crm="isLoadingCrm"
          :is-saving-crm="isSavingCrm"
          :is-testing-crm="isTestingCrm"
          :is-activating-crm="isActivatingCrm"
          :can-update-settings="canUpdateSettings"
          @save="salvarCrmConfig"
          @test="testarCrmConfig"
          @activate="ativarCrmConfig"
        />
      </div>

      <!-- Tab WhatsApp -->
      <div
        v-if="tab === 'whatsapp'"
        :id="tabPanelId('whatsapp')"
        role="tabpanel"
        :aria-labelledby="tabButtonId('whatsapp')"
        tabindex="0"
        class="grid w-full gap-5"
      >
        <div
          class="flex flex-wrap gap-2"
          role="tablist"
          aria-label="Seções de WhatsApp"
        >
          <button
            v-for="subTab in whatsappSubTabs"
            :id="whatsappSubTabButtonId(subTab.id)"
            :key="subTab.id"
            type="button"
            role="tab"
            :aria-selected="whatsappSubTab === subTab.id"
            :aria-controls="whatsappSubTabPanelId(subTab.id)"
            :tabindex="whatsappSubTab === subTab.id ? 0 : -1"
            class="inline-flex h-10 items-center rounded-full px-4 text-[13px] font-semibold transition-colors"
            :class="
              whatsappSubTab === subTab.id
                ? 'bg-lais-primary text-white'
                : 'bg-white border border-[#e5e7eb] text-lais-text-body hover:border-lais-primary/40'
            "
            @click="selectWhatsappSubTab(subTab.id)"
            @keydown="event => handleWhatsappSubTabKeydown(event, subTab.id)"
          >
            {{ subTab.label }}
          </button>
        </div>

        <div
          v-if="whatsappSubTab === 'conexao_qr'"
          :id="whatsappSubTabPanelId('conexao_qr')"
          role="tabpanel"
          :aria-labelledby="whatsappSubTabButtonId('conexao_qr')"
          tabindex="0"
          class="grid w-full gap-5"
        >
          <MinhaLaisWhatsappQrPanel
            v-model:whatsapp-selected-owner-id="whatsappSelectedOwnerId"
            :whatsapp-meta="whatsappMeta"
            :whatsapp-qr="whatsappQr"
            :whatsapp-agent-options="whatsappAgentOptions"
            :whatsapp-connections="whatsappConnections"
            :active-whatsapp-connections="activeWhatsappConnections"
            :is-whatsapp-pairing-busy="isWhatsappPairingBusy"
            :is-generating-whatsapp-qr="isGeneratingWhatsappQr"
            :is-loading-whatsapp-connections="isLoadingWhatsappConnections"
            :refreshing-connection-id="refreshingConnectionId"
            :updating-connection-id="updatingConnectionId"
            :deleting-connection-id="deletingConnectionId"
            @generate-qr="gerarWhatsappQr"
            @renew-qr="renovarWhatsappQr"
            @change-owner="trocarResponsavelWhatsapp"
            @remove-connection="removerWhatsappConnection"
          />
        </div>

        <div
          v-if="whatsappSubTab === 'onboarding_meta'"
          :id="whatsappSubTabPanelId('onboarding_meta')"
          role="tabpanel"
          :aria-labelledby="whatsappSubTabButtonId('onboarding_meta')"
          tabindex="0"
          class="grid w-full gap-5"
        >
          <MinhaLaisWhatsappOnboardingPanel
            v-model="whatsappGovernance"
            :whatsapp-checklist-items="whatsappChecklistItems"
            :whatsapp-checklist-progress="whatsappChecklistProgress"
            :is-loading-whatsapp-governance="isLoadingWhatsappGovernance"
            :is-saving-whatsapp-onboarding="isSavingWhatsappOnboarding"
            @save="salvarWhatsappOnboarding"
          />
        </div>

        <div
          v-if="whatsappSubTab === 'perfil'"
          :id="whatsappSubTabPanelId('perfil')"
          role="tabpanel"
          :aria-labelledby="whatsappSubTabButtonId('perfil')"
          tabindex="0"
          class="grid w-full gap-5"
        >
          <MinhaLaisWhatsappProfilePanel
            v-model="whatsappGovernance"
            v-model:whatsapp-requested-display-name="
              whatsappRequestedDisplayName
            "
            :whatsapp-connection-options="whatsappConnectionOptions"
            :whatsapp-photo-file="whatsappPhotoFile"
            :is-loading-whatsapp-governance="isLoadingWhatsappGovernance"
            :is-saving-whatsapp-profile="isSavingWhatsappProfile"
            :is-uploading-whatsapp-photo="isUploadingWhatsappPhoto"
            :is-requesting-whatsapp-display-name="
              isRequestingWhatsappDisplayName
            "
            @save="salvarWhatsappProfile"
            @select-photo="selecionarWhatsappPhoto"
            @upload-photo="enviarWhatsappProfilePhoto"
            @request-display-name="solicitarWhatsappDisplayName"
          />
        </div>

        <div
          v-if="whatsappSubTab === 'operacoes'"
          :id="whatsappSubTabPanelId('operacoes')"
          role="tabpanel"
          :aria-labelledby="whatsappSubTabButtonId('operacoes')"
          tabindex="0"
          class="grid w-full gap-5"
        >
          <MinhaLaisWhatsappOperationsPanel
            :whatsapp-connections="whatsappConnections"
            :can-manage="whatsappMeta.admin"
            @open-connection="selectWhatsappSubTab('conexao_qr')"
          />
        </div>
      </div>

      <!-- Seção Geral: Origens -->
      <div v-if="tab === 'geral'" class="grid w-full gap-5">
        <MinhaLaisOrigensCard v-model="form" />
      </div>

      <!-- Seção Geral: Notificações -->
      <div v-if="tab === 'geral'" class="grid w-full gap-5">
        <MinhaLaisNotificacoesCard
          v-model:notification-search="notificationSearch"
          :notification-contact-id="notificationContactId"
          :notification-contact-options="notificationContactOptions"
          :selected-notification-contact="selectedNotificationContact"
          :notification-profile="notificationProfile"
          :consents="consents"
          :is-saving-consents="isSavingConsents"
          @search-input="carregarNotificationContacts"
          @contact-change="onNotificationContactChange"
          @profile-select="selectNotificationProfile"
          @consent-change="salvarConsent"
          @apply-all-profiles="aplicarTodosOsPerfis"
          @tooltip-click="trackTooltipClick"
        />
      </div>

      <!-- Seção Geral: Aparência -->
      <div v-if="tab === 'geral'" class="grid w-full gap-5">
        <MinhaLaisAparenciaCard
          :dark-mode-enabled="darkModeEnabled"
          @toggle-dark-mode="toggleDarkMode"
        />
      </div>
    </div>

    <LaisConfirmModal
      :show="!!connectionToRemove"
      title="Remover conexão de WhatsApp"
      :description="`Remover a conexão ${connectionRemovalLabel(connectionToRemove)}? Essa ação encerra o vínculo do WhatsApp com a Imudei.`"
      confirm-text="Remover conexão"
      :is-loading="deletingConnectionId === connectionToRemove?.id"
      @close="cancelarRemoverWhatsappConnection"
      @confirm="confirmarRemoverWhatsappConnection"
    />
  </section>
</template>
